import { useEffect, useState } from "react";
import { Reportable } from "./ReportCreation";

interface Filter<U> {
  filterFields: Array<U>;
  jointTable: boolean;
  filterParamName: keyof Reportable;
}

export interface FilterOutputTable<T extends Reportable> {
  header: string;
  data: T[];
}

export interface FilterOutput<T extends Reportable> {
  filename: string;
  tables: Array<FilterOutputTable<T>>;
}

export function useReportFilter<T extends Reportable>(
  reportType: string,
  data: Array<T>
) {
  const [classFilter, setClassFilter] = useState<Filter<string>>({
    filterFields: [],
    jointTable: true,
    filterParamName: "class" as keyof Reportable,
  });
  const [startgroupFilter, setStartgroupFilter] = useState<Filter<Date>>({
    filterFields: [],
    jointTable: true,
    filterParamName: "startgroup" as keyof Reportable,
  });
  const [genderFilter, setGenderFilter] = useState<Filter<string>>({
    filterFields: [],
    jointTable: true,
    filterParamName: "gender" as keyof Reportable,
  });

  const [filteredData, setFilteredData] = useState<Array<Array<T>>>([data]);
  const [tableNames, setTableName] = useState<Array<string>>([]);

  const [filterOutput, setFilterOutput] = useState<FilterOutput<T>>({
    filename: reportType,
    tables: [],
  });

  useEffect(() => {
    setTableName(
      createTableNames(reportType, classFilter, genderFilter, startgroupFilter)
    );
    setFilteredData((old) => {
      const effectiveClassFilters = getEffectiveFilter(classFilter, data);
      const effectiveStartgroupFilters = getEffectiveFilter(
        startgroupFilter,
        data
      );
      const effectiveGenderFilters = getEffectiveFilter(genderFilter, data);

      return filterReportable(
        data,
        effectiveClassFilters,
        effectiveStartgroupFilters,
        effectiveGenderFilters
      );
    });
  }, [data, classFilter, startgroupFilter, genderFilter, reportType]);

  useEffect(() => {
    setFilterOutput({
      filename: reportType,
      tables: filteredData.map((data, index) => {
        return {
          data: data,
          header: tableNames[index],
        } as FilterOutputTable<T>;
      }),
    });
  }, [tableNames, filteredData]);

  return {
    setClassFilter,
    setStartgroupFilter,
    setGenderFilter: setGenderFilter,
    filterOutput,
  };
}

function getEffectiveFilter<T extends Reportable, U>(
  inputFilter: Filter<U>,
  data: Array<T>
) {
  const effectiveFilters = [];
  if (inputFilter.jointTable) {
    effectiveFilters.push(inputFilter.filterFields);
  } else {
    if (inputFilter.filterFields.length > 0) {
      inputFilter.filterFields.forEach((f) => {
        effectiveFilters.push([f]);
      });
    } else {
      new Set(data.map((d) => d[inputFilter.filterParamName])).forEach((c) => {
        effectiveFilters.push([c]);
      });
    }
  }
  return effectiveFilters;
}

function createTableNames(
  reportType: string,
  classFilter: Filter<string>,
  genderFilter: Filter<string>,
  startgroupFilter: Filter<Date>
) {
  var tableNames: Array<string> = [reportType];
  if (classFilter.filterFields.length > 0) {
    if (classFilter.jointTable && classFilter.filterFields.length > 0) {
      tableNames = [`${reportType} ${classFilter.filterFields.join(", ")} & `];
    } else {
      tableNames = classFilter.filterFields.map(
        (name) => `${reportType} ${name} & `
      );
    }
  }

  if (genderFilter.filterFields.length > 0) {
    if (genderFilter.jointTable && genderFilter.filterFields.length > 0) {
      tableNames = tableNames.map(
        (h) => `${h} ${genderFilter.filterFields.join(", ")} & `
      );
    } else {
      tableNames = tableNames.flatMap((h) =>
        genderFilter.filterFields.map((s) => `${h} ${s} & `)
      );
    }
  }

  if (startgroupFilter.filterFields.length > 0) {
    if (startgroupFilter.jointTable) {
      tableNames = tableNames.map(
        (h) =>
          `${h} ${startgroupFilter.filterFields
            .map(
              (date) =>
                `${date.getHours().toString().padStart(2, "0")}h${date
                  .getMinutes()
                  .toString()
                  .padStart(2, "0")}m`
            )
            .join(", ")}`
      );
    } else {
      const tempNames: string[] = [];
      tableNames.forEach((name) => {
        startgroupFilter.filterFields.forEach((startgroup) => {
          tempNames.push(
            `${name} ${startgroup
              .getHours()
              .toString()
              .padStart(2, "0")}h${startgroup
              .getMinutes()
              .toString()
              .padStart(2, "0")}m`
          );
        });
      });
      tableNames = tempNames;
    }
  }

  return tableNames.length > 0
    ? tableNames.map((name) =>
        name.replace(/\s*&*\s*$/, "").replaceAll(/\s+/g, " ")
      )
    : [reportType];
}

function filterReportable<T extends Reportable>(
  data: Array<T>,
  classesCollections: Array<Array<string>>,
  startgroupCollections: Array<Array<Date>>,
  genderCollections: Array<Array<string>>
) {
  const filteredData: Array<Array<T>> = [];
  classesCollections.forEach((classes) => {
    startgroupCollections.forEach((startgroups) => {
      genderCollections.forEach((gender) => {
        // First, get the base filtered data
        let filterResult = data.filter(
          (e) => {
            const matchesClass = classes.length === 0 || classes.includes(e.disciplineName);
            const matchesStartgroup = startgroups.length === 0 ||
              startgroups
                .map((d) => d.getTime())
                .includes(e.startgroup?.getTime() ?? -1);
            const matchesGender = gender.length === 0 || gender.includes(e.gender ?? "");
            
            return matchesClass && matchesStartgroup && matchesGender;
          }
        );

        // Special case: if 'M' is in the base filtered results, also include an additional copy of JL/MAN as discipline M
        // That way JL/MAN remains in JL from the base filter and is also duplicated into M.
        const hasMInResults = filterResult.some((e) => e.disciplineName === 'M');
        if (hasMInResults) {
          const jlManResults = data
            .filter((e) => {
              const matchesStartgroup = startgroups.length === 0 ||
                startgroups
                  .map((d) => d.getTime())
                  .includes(e.startgroup?.getTime() ?? -1);
              const matchesGender = gender.length === 0 || gender.includes(e.gender ?? "");
              return e.disciplineName === 'JL' && e.gender === 'MAN' && matchesStartgroup && matchesGender;
            })
            .map((e) => ({ ...e, disciplineName: 'M', __originalDiscipline: 'JL' } as any as T));
          filterResult = [...filterResult, ...jlManResults];
        }

        // Special case: if 'K' is in the base filtered results, also include an additional copy of JL/WOMAN as discipline K
        // That way JL/WOMAN remains in JL from the base filter and is also duplicated into K.
        const hasKInResults = filterResult.some((e) => e.disciplineName === 'K');
        if (hasKInResults) {
          const jlWomanResults = data
            .filter((e) => {
              const matchesStartgroup = startgroups.length === 0 ||
                startgroups
                  .map((d) => d.getTime())
                  .includes(e.startgroup?.getTime() ?? -1);
              const matchesGender = gender.length === 0 || gender.includes(e.gender ?? "");
              return e.disciplineName === 'JL' && e.gender === 'WOMAN' && matchesStartgroup && matchesGender;
            })
            .map((e) => ({ ...e, disciplineName: 'K', __originalDiscipline: 'JL' } as any as T));
          filterResult = [...filterResult, ...jlWomanResults];
        }

        // Special case: if UL entries match age ranges, duplicate them to P15, P13, F15, F13
        // P15/F15: age 14-15, P13/F13: age 12-13
        // MAN -> P, WOMAN -> F
        const pAndFDisciplines = ['P15', 'P13', 'F15', 'F13'];
        const hasPOrFInResults = filterResult.some((e) => pAndFDisciplines.includes(e.disciplineName));
        if (hasPOrFInResults) {
          const ulResults = data.filter(
            (e) => {
              const matchesStartgroup = startgroups.length === 0 ||
                startgroups
                  .map((d) => d.getTime())
                  .includes(e.startgroup?.getTime() ?? -1);
              const matchesGender = gender.length === 0 || gender.includes(e.gender ?? "");
              return e.disciplineName === 'UL' && matchesStartgroup && matchesGender;
            }
          );

          ulResults.forEach((ulEntry) => {
            const currentYear = 2026;
            const age = currentYear - (ulEntry as any).birthYear;
            const isMale = ulEntry.gender === 'MAN';
            const targetDisciplines: string[] = [];

            // Determine which disciplines this UL entry should be added to
            if (age >= 14 && age <= 15) {
              targetDisciplines.push(isMale ? 'P15' : 'F15');
            }
            if (age <= 13) {
              targetDisciplines.push(isMale ? 'P13' : 'F13');
            }

            // Add duplicates for matching disciplines
            targetDisciplines.forEach((targetDiscipline) => {
              if (filterResult.some((e) => e.disciplineName === targetDiscipline)) {
                filterResult.push({
                  ...ulEntry,
                  disciplineName: targetDiscipline,
                  __originalDiscipline: 'UL'
                } as any as T);
              }
            });
          });
        }

        filteredData.push(filterResult);
      });
    });
  });
  return filteredData;
}
